/**
 * Progressivity Service
 * HTTP operations for attendance progressivities (Ley 40 horas)
 */

import { apiClient, API_CONFIG } from '@/api/core';
import type { ApiResponse } from '@/api/shared';
import type { LC_ProgressivityItemOut, LC_CreateProgressivityIn, LC_UpdateProgressivityIn, Progressivity } from '../types';
import { mapProgressivityItem } from '../mappers';


export class ProgressivityService {
  private get endpoint(): string {
    return API_CONFIG.ENDPOINTS.ATTENDANCE.PROGRESSIVITIES;
  }

  /**
   * Get progressivities list
   * GET /v{version}/attendance/configuration/progressivities
   */
  async getProgressivities(): Promise<ApiResponse<Progressivity[]>> {
    const response = await apiClient.get<LC_ProgressivityItemOut[]>(this.endpoint);

    if (!response.success || !response.data) {
      return { ...response, data: null };
    }

    return {
      ...response,
      data: response.data.map(mapProgressivityItem),
    };
  }

  /**
   * Create a progressivity
   * POST /v{version}/attendance/configuration/progressivities
   */
  async createProgressivity(input: LC_CreateProgressivityIn): Promise<ApiResponse<null>> {
    const response = await apiClient.post<null>(this.endpoint, input);
    return { ...response, data: null };
  }

  /**
   * Update a progressivity
   * PUT /v{version}/attendance/configuration/progressivities/{startDate}
   */
  async updateProgressivity(
    originalStartDate: string,
    input: LC_UpdateProgressivityIn,
  ): Promise<ApiResponse<null>> {
    const url = `${this.endpoint}/${encodeURIComponent(originalStartDate)}`;
    const response = await apiClient.put<null>(url, input);
    return { ...response, data: null };
  }

  /**
   * Delete a progressivity
   * DELETE /v{version}/attendance/configuration/progressivities/{startDate}
   */
  async deleteProgressivity(startDate: string): Promise<ApiResponse<null>> {
    const url = `${this.endpoint}/${encodeURIComponent(startDate)}`;
    const response = await apiClient.delete<null>(url);
    return { ...response, data: null };
  }
}

export const progressivityService = new ProgressivityService();
